import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Button from '@travel/design-system/core/src/components/Button';
import TextField from '@travel/design-system/core/src/components/TextField';
import { getMarket } from '@travel/i18n';
import { ArrowDown, ArrowUp, Clear, PlusOutlined, Trash } from '@travel/icons/ui';
import { Translate } from '@travel/translation';
import { useDeviceTypes, useKeyboard, useTranslation } from '@travel/traveler-core/hooks';
import { cx } from '@travel/utils';
import { defaultNumberFormatter } from '@travel/utils/src/numberFormatter';

import { SNS_PLATFORM_LABELS } from 'constants/userProfile';
import { getSNSIcons } from 'utils/snsIcons';

import {
  getPathFromUrl,
  splitDomainAndPath,
  trimUrlProtocolSubDomain,
  SNS_VALIDATION_LIST,
} from 'pages/MyReviewEdit/helper';
import { SnsPlatform } from 'store/apiTypes';
import { useAppSelector } from 'store/hooks';

import styles from './snsUserNameEditor.module.scss';

const snsIconsList = getSNSIcons(24);

type Props = {
  platform: SnsPlatform;
  prefixUrl: string;
  handleDelete: (platform: SnsPlatform) => void;
  updateData: (platform: SnsPlatform, val: string) => void;
  addSNSLink?: (obj: { platform: SnsPlatform; url: string }) => void;
  isDisplayedLinksSection?: boolean;
};

const SnsUserNameEditor: React.FC<Props> = ({
  platform,
  prefixUrl,
  handleDelete,
  updateData,
  addSNSLink,
  isDisplayedLinksSection = true,
}) => {
  const [domainNoSlash, userInput] = splitDomainAndPath(
    trimUrlProtocolSubDomain(prefixUrl, platform),
  );
  const domain = domainNoSlash + '/';

  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(trimUrlProtocolSubDomain(prefixUrl, platform));
  const [userInputValue, setUserInputValue] = useState(userInput);
  const ICON_SIZE = 24;
  const ICON_SMALL_SIZE = 20;
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);
  const market = useAppSelector(getMarket);

  const SNSplaceholder = useTranslation({ id: 'MyReview.Edit.UserID' });
  const [initialSNSUrl] = useState(prefixUrl);
  const { isPC } = useDeviceTypes();
  const toggleAccordion = useCallback(() => {
    setIsOpen((prevVal) => !prevVal);
  }, [setIsOpen]);

  const getPathName = () => {
    const parts = inputValue.split('/');

    return parts[parts.length - 1];
  };

  useEffect(() => {
    const validateInputValue = (val: string) => {
      const validations = SNS_VALIDATION_LIST[platform];
      const value = getPathFromUrl(val);

      return !validations.regex?.test(value);
    };

    setIsButtonDisabled(validateInputValue(inputValue) ?? true);
  }, [inputValue, platform]);
  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const val = event.target.value;

      updateData(platform, `${domain}${val}`);
      setInputValue(`${domain}${val}`);
      setUserInputValue(val);
    },
    [domain, platform, updateData],
  );

  const clearInputField = useCallback(() => {
    setInputValue(domain);
    updateData(platform, domain);
    setIsButtonDisabled(true);
    setUserInputValue('');
  }, [domain, updateData, platform]);

  const focusInput = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    inputRef.current?.focus();
  }, []);

  const handleKeyDownAccordion = useKeyboard(
    useMemo(
      () => ({
        Submit: (event) => {
          event.preventDefault();
          toggleAccordion();
        },
      }),
      [toggleAccordion],
    ),
  );

  const handleKeyDownPlatformIn = useKeyboard(
    useMemo(
      () => ({
        Submit: (event) => focusInput(event),
      }),
      [focusInput],
    ),
  );

  return (
    <div className={styles.accordion} data-testid="my-review-edit-sns-edit">
      <div
        className={styles.accordionHeader}
        onClick={toggleAccordion}
        onKeyDown={handleKeyDownAccordion}
        role="button"
        tabIndex={0}
        data-testid="my-review-edit-sns-accordion-button"
      >
        <div className={styles.titleSection}>
          <span className={styles.icon} data-testid="my-review-edit-sns-modal-icon">
            {snsIconsList[platform]}
          </span>
          <span className={styles.title} data-testid="my-review-edit-sns-modal-title">
            {isDisplayedLinksSection && (isPC || !isOpen) ? (
              trimUrlProtocolSubDomain(initialSNSUrl)
            ) : (
              <Translate id={SNS_PLATFORM_LABELS[platform]} />
            )}
          </span>
        </div>
        <div className={styles.arrowSection}>
          {isOpen ? (
            isDisplayedLinksSection ? (
              <ArrowUp size={ICON_SIZE} data-testid="my-review-edit-sns-arrow-up" />
            ) : (
              <Clear
                size={ICON_SIZE}
                className={styles.iconBlack}
                data-testid="my-review-edit-sns-close"
                onClick={clearInputField}
              />
            )
          ) : isDisplayedLinksSection ? (
            <ArrowDown size={ICON_SIZE} data-testid="my-review-edit-sns-arrow-down" />
          ) : (
            <PlusOutlined size={ICON_SIZE} data-testid="my-review-edit-sns-add" />
          )}
        </div>
      </div>
      <div className={cx(styles.accordionContent, isOpen && styles.accordionContentOpen)}>
        <div className={styles.inputContainer}>
          <TextField data-testid="my-review-edit-sns-modal-input">
            <TextField.InputWrapper
              hasError={isButtonDisabled && getPathName().length !== 0}
              className={styles.inputField}
              htmlFor={`label-${platform}-id`}
              as="label"
              onClick={focusInput}
              onKeyDown={handleKeyDownPlatformIn}
            >
              <span
                className={styles.snsDomainLabel}
                data-testid="my-review-edit-sns-domain-label"
                onClick={focusInput}
                onKeyDown={handleKeyDownPlatformIn}
                role="button"
                tabIndex={0}
              >
                {domain + (SnsPlatform.TIKTOK === platform ? '@' : '')}
              </span>
              <TextField.Input
                id={`label-${platform}-id`}
                onChange={handleInputChange}
                value={userInputValue}
                type="text"
                autoComplete="off"
                placeholder={SNSplaceholder}
                className={styles.snsInput}
                forwardRef={inputRef}
                name={`snsInput-${platform}`}
              />
              {userInputValue.length > 0 ? (
                <TextField.ClearButton handleClearClick={clearInputField} />
              ) : null}
            </TextField.InputWrapper>
            <TextField.HelperWrapper>
              <TextField.Error
                hasError={isButtonDisabled && userInputValue.length !== 0}
                isAttentionIconVisible={false}
              >
                <Translate id="MyReview.EditSNSLink.InvalidInput" />
              </TextField.Error>
              <TextField.Count
                maxLength={SNS_VALIDATION_LIST[platform].maxLength ?? undefined}
                value={userInputValue}
                countFormatter={defaultNumberFormatter(market)}
              />
            </TextField.HelperWrapper>
          </TextField>
        </div>

        <Button
          data-testid="snsUserNameEditor-actionButton"
          variant={isDisplayedLinksSection ? 'outlined' : 'filled'}
          className={styles.actionButton}
          onClick={() =>
            isDisplayedLinksSection
              ? handleDelete(platform)
              : addSNSLink && addSNSLink({ platform, url: inputValue })
          }
          isDisabled={isDisplayedLinksSection ? false : isButtonDisabled}
        >
          <span className={styles.icon}>
            {isDisplayedLinksSection ? (
              <Trash className={cx(styles.actionIcon)} size={ICON_SMALL_SIZE} color="cilantro" />
            ) : (
              <PlusOutlined
                color="darkCyan"
                size={ICON_SMALL_SIZE}
                className={cx(styles.iconWhite, styles.actionIcon)}
              />
            )}
          </span>
          {isDisplayedLinksSection ? (
            <Translate id="Review_Common.Delete" />
          ) : (
            <Translate id="Review_Common.Add" />
          )}
        </Button>
      </div>
    </div>
  );
};

export default SnsUserNameEditor;
